import { VatStatus } from '@jalupro/shared';

/**
 * A compact VAT-number cell: the number rendered with a leading validity dot.
 *   • green  — VALID (verified against VIES)
 *   • red    — INVALID
 *   • amber  — UNAVAILABLE (VIES was unreachable; retry later)
 *   • grey   — UNVERIFIED / NOT_APPLICABLE (non-EU, never checked)
 *
 * `title` is the native hover tooltip (the screens compose it with the localized
 * status + last-checked date). When there's no VAT number we render an em dash and
 * no dot.
 */

const DOT_COLOR: Record<VatStatus, string> = {
  [VatStatus.VALID]: 'var(--green)',
  [VatStatus.INVALID]: 'var(--red)',
  [VatStatus.UNAVAILABLE]: 'var(--amber)',
  [VatStatus.UNVERIFIED]: 'var(--line)',
  [VatStatus.NOT_APPLICABLE]: 'var(--line)',
};

export interface VatDotProps {
  vatNumber?: string | null;
  status: VatStatus;
  /** Native tooltip shown on hover (localized status + check date). */
  title?: string;
  placeholder?: string;
}

export function VatDot({ vatNumber, status, title, placeholder = '—' }: VatDotProps) {
  const value = vatNumber?.trim();
  if (!value) {
    return (
      <span className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>
        {placeholder}
      </span>
    );
  }
  return (
    <span
      className="row gap-6 center"
      style={{ display: 'inline-flex' }}
      title={title}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: DOT_COLOR[status],
          flex: '0 0 auto',
          cursor: title ? 'help' : 'default',
        }}
      />
      <span className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>
        {value}
      </span>
    </span>
  );
}
